/**
 * Ticket type definitions
 * Shared between frontend and backend
 */

import { IUser } from './user.types';

export enum TicketCategory {
  BUG = 'bug',
  FEATURE = 'feature',
  SUPPORT = 'support',
  QUESTION = 'question',
  OTHER = 'other',
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
  REOPENED = 'reopened',
}

export enum TicketAction {
  CREATED = 'created',
  UPDATED = 'updated',
  ASSIGNED = 'assigned',
  STATUS_CHANGED = 'status_changed',
  COMMENTED = 'commented',
  CLOSED = 'closed',
  REOPENED = 'reopened',
  ATTACHMENT_ADDED = 'attachment_added',
  ATTACHMENT_REMOVED = 'attachment_removed',
  WATCHER_ADDED = 'watcher_added',
  WATCHER_REMOVED = 'watcher_removed',
}

export interface IAttachment {
  _id?: string;
  fileName: string;
  originalName: string;
  fileUrl: string;
  s3Key?: string; // Optional for backward compatibility
  s3Bucket?: string; // Optional for backward compatibility
  fileSize: number;
  mimeType: string;
  uploadedBy: IUser | string;
  uploadedAt: Date;
}

export interface ISLA {
  responseTime: number; // in minutes
  resolutionTime: number; // in minutes
  responseDeadline: Date;
  resolutionDeadline: Date;
  breached: boolean;
  breachNotificationSent: boolean;
}

export interface ITicket {
  _id: string;
  ticketNumber: string;
  title: string;
  description?: string; // Make description optional
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  tags: string[];
  createdBy: IUser | string;
  assignedTo?: IUser | string;
  assignedTeam?: string;
  watchers: (IUser | string)[];
  attachments: IAttachment[];
  sla: ISLA;
  resolution?: string;
  resolvedBy?: IUser | string;
  resolvedAt?: Date;
  closedAt?: Date;
  reopenCount: number;
  viewCount: number;
  commentsCount: number;
  isOverdue: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ITicketComment {
  _id: string;
  ticketId: string;
  userId: IUser | string;
  content: string;
  isInternal: boolean;
  mentions: (IUser | string)[];
  attachments: IAttachment[];
  editedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ITicketHistory {
  _id: string;
  ticketId: string;
  userId: IUser | string;
  action: TicketAction;
  field?: string;
  oldValue?: any;
  newValue?: any;
  description: string;
  createdAt: Date;
}

export interface ISLAConfig {
  _id?: string;
  priority: TicketPriority;
  responseTime: number; // in minutes
  resolutionTime: number; // in minutes
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface TicketFilters {
  status?: TicketStatus[];
  priority?: TicketPriority[];
  category?: TicketCategory[];
  assignedTo?: string;
  createdBy?: string;
  watchers?: string;
  search?: string;
  startDate?: Date;
  endDate?: Date;
  slaBreached?: boolean;
  myTickets?: boolean;
  watching?: boolean;
  overdue?: boolean;
  tags?: string[];
}

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  reopened: number;
  breached: number;
  averageResolutionTime: number; // in hours
  myAssigned: number;
  overdue: number;
  byPriority: Record<TicketPriority, number>;
  byCategory: Record<TicketCategory, number>;
  byStatus: Record<TicketStatus, number>;
  trendData: {
    date: string;
    created: number;
    resolved: number;
  }[];
}

// File interface for cross-platform compatibility
export interface FileUpload {
  name: string;
  size: number;
  type: string;
  lastModified?: number;
}

export interface CreateTicketData {
  title: string;
  description?: string; // Make description optional
  category: TicketCategory;
  priority: TicketPriority;
  assignedTo?: string;
  assignedTeam?: string;
  watchers?: string[];
  tags?: string[];
  attachments?: FileUpload[];
}

export interface UpdateTicketData {
  title?: string;
  description?: string;
  category?: TicketCategory;
  priority?: TicketPriority;
  status?: TicketStatus;
  assignedTo?: string;
  assignedTeam?: string;
  watchers?: string[];
  tags?: string[];
  resolution?: string;
}

export interface CreateCommentData {
  content: string;
  isInternal?: boolean;
  attachments?: FileUpload[];
}

export interface BulkUpdateData {
  ticketIds: string[];
  updateData: {
    status?: TicketStatus;
    assignedTo?: string;
    priority?: TicketPriority;
  };
}

export interface TicketExportParams {
  filters?: TicketFilters;
  format: 'csv' | 'excel' | 'pdf';
  fields?: string[];
}

// API Response types
export interface TicketDetailResponse {
  ticket: ITicket;
  comments: ITicketComment[];
  history: ITicketHistory[];
}

// Socket.io event types
export interface TicketSocketEvents {
  'ticket:created': (ticket: ITicket) => void;
  'ticket:updated': (ticket: ITicket) => void;
  'ticket:assigned': (data: { ticket: ITicket; assignedTo: IUser }) => void;
  'ticket:status_changed': (data: {
    ticket: ITicket;
    oldStatus: TicketStatus;
    newStatus: TicketStatus;
  }) => void;
  'ticket:commented': (data: { ticket: ITicket; comment: ITicketComment }) => void;
  'ticket:sla_warning': (ticket: ITicket) => void;
  'ticket:sla_breached': (ticket: ITicket) => void;
}

// Form validation schemas (compatible with Zod)
export const TicketValidationRules = {
  title: {
    min: 5,
    max: 200,
  },
  description: {
    min: 0, // Allow empty description
    max: 5000,
  },
  comment: {
    min: 1,
    max: 2000,
  },
  tags: {
    max: 10,
    tagLength: 50,
  },
  attachment: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv',
    ],
  },
} as const;
